import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building2, 
  User, 
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  ChevronDown,
  Plus,
  Save,
  X,
  Trash2,

  FileText,
  Loader2
} from 'lucide-react';
import { 
  Button,
  ProgressBar,
  ConfirmDialog
} from '../components/ui';
import { TaskCard } from '../components/TaskCard';
import { RichTextEditor } from '../components/RichTextEditor';
import { FeedbackSection } from '../components/FeedbackSection';
import { FeedbackForm } from '../components/FeedbackForm';
import { HotelWithProgress, Task, Category, Feedback } from '../types';
import { apiService } from '../services/api';
import { formatDate, hotelNameToSlug } from '../utils/format';
import { useUser } from '../contexts/UserContext';
import { logger } from '../utils/logger';

export function HotelDetail() {
  const { hotelSlug } = useParams<{ hotelSlug: string }>();
  const navigate = useNavigate();
  const { isAdmin, isManagement, isHotelStaff, assignedHotelIds, hasAccessToHotel, isLoading: userLoading } = useUser();
  
  const [hotel, setHotel] = useState<HotelWithProgress | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [showAddTask, setShowAddTask] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);
  const [showMarkAllCompleteDialog, setShowMarkAllCompleteDialog] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState<Feedback | null>(null);
  const [deletingFeedbackId, setDeletingFeedbackId] = useState<string | null>(null);

  useEffect(() => {
    if (hotelSlug && !userLoading) {
      loadHotelData();
    }
  }, [hotelSlug, userLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Optimized task update handler with memoization and error boundaries
  const handleTaskUpdate = useCallback((updatedTasks: Task[]) => {
    try {
      setTasks(prevTasks => {
        // Quick length check first
        if (prevTasks.length !== updatedTasks.length) {
          return updatedTasks;
        }
        
        // Use hash-based change detection for better performance
        const prevHash = prevTasks.reduce((hash, task) => {
          return hash + task.id + task.status + task.comments.length + task.files.length;
        }, '');
        
        const updatedHash = updatedTasks.reduce((hash, task) => {
          return hash + task.id + task.status + task.comments.length + task.files.length;
        }, '');
        
        if (prevHash === updatedHash) {
          return prevTasks; // No change, return same reference
        }
        
        return updatedTasks;
      });
    } catch (error) {
      logger.error('Error in task update handler:', error);
      // Don't crash the component, just log the error
    }
  }, []);

  // Set up real-time listeners for tasks only (hotel progress calculated from database)
  useEffect(() => {
    if (!hotel) return;

    // Subscribe to task updates with optimized handler
    const tasksUnsubscribe = apiService.subscribeToHotelTasks(hotel.id, handleTaskUpdate);

    // Cleanup subscriptions on unmount or hotel change
    return () => {
      tasksUnsubscribe();
    };
  }, [hotel?.id, handleTaskUpdate]); // eslint-disable-line react-hooks/exhaustive-deps



  // Initialize collapsed sections when categories are loaded
  useEffect(() => {
    if (categories.length > 0) {
      const initialCollapsedSections: Record<string, boolean> = {};
      categories.forEach(category => {
        initialCollapsedSections[category.id] = true;
      });
      setCollapsedSections(initialCollapsedSections);
    }
  }, [categories]);



  // Memoized task stats to prevent recalculation
  const taskStats = useMemo(() => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.status === 'completed').length;
    const inProgressTasks = tasks.filter(t => t.status === 'in-progress').length;
    const blockedTasks = tasks.filter(t => t.status === 'blocked').length;
    const notStartedTasks = tasks.filter(t => t.status === 'not-started').length;
    
    return {
      totalTasks,
      completedTasks,
      inProgressTasks,
      blockedTasks,
      notStartedTasks
    };
  }, [tasks]);

  // Memoized sorted categories
  const sortedCategories = useMemo(() => {
    return categories.sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [categories]);

  const loadHotelData = async () => {
    if (!hotelSlug) return;
    
    try {
      setLoading(true);
      
      // Get all hotels and find the one matching the slug
      const allHotels = await apiService.getHotels();
      const hotelData = allHotels.find(hotel => hotelNameToSlug(hotel.name) === hotelSlug);
      
      if (!hotelData) {
        setError('Hotel not found');
        setLoading(false);
        return;
      }

      // Check if user has access to this hotel
      if (!hasAccessToHotel(hotelData.id)) {
        setError('You do not have access to this hotel. Please contact your administrator.');
        setLoading(false);
        return;
      }
      
      // Load categories and hotel with progress calculation from database
      const [categoriesData, hotelWithProgress] = await Promise.all([
        apiService.getCategories(),
        apiService.getHotel(hotelData.id) // This now includes progress calculation
      ]);
      
      logger.log(`🔍 Loading hotel: ${hotelWithProgress.id} (${hotelWithProgress.name})`);
      logger.log(`📂 Loaded ${categoriesData.length} categories:`, categoriesData);
      logger.log(`📊 Hotel progress: ${hotelWithProgress.progressPercentage}% (${hotelWithProgress.completedTasks}/${hotelWithProgress.totalTasks} tasks)`);
      
      setCategories(categoriesData);
      setHotel(hotelWithProgress);
      setError(null);
    } catch (err) {
      setError('Failed to load hotel data');
      logger.error('Error loading hotel data:', err);
    } finally {
      setLoading(false);
    }
  };





  const handleAddTask = async (taskData: Partial<Task>) => {
    if (!hotel) return;
    
    try {
      const newTask = await apiService.createTask({
        ...taskData,
        hotelId: hotel.id,
        status: 'not-started',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        assignedTo: '',
        comments: []
      } as Task);
      
      setTasks(prev => [...prev, newTask]);
      setShowAddTask(false);
    } catch (err) {
      logger.error('Error creating task:', err);
      // For now, we'll create a mock task since we don't have the API yet
      const mockTask: Task = {
        id: `task-${Date.now()}`,
        hotelId: hotel.id,
        title: taskData.title || '',
        description: taskData.description || '',
        status: 'not-started',
        priority: taskData.priority || 'medium',
        dueDate: taskData.dueDate || '',
        category: taskData.category || 'setup',
        order: 1, // Default order for mock tasks
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        assignedTo: '',
        comments: [],
        files: []
      };
      setTasks(prev => [...prev, mockTask]);
      setShowAddTask(false);
    }
  };

  const handleEditTask = async (taskData: Partial<Task>) => {
    if (!editingTask) return;
    
    try {
      const updatedTask = await apiService.updateTask(editingTask.id, taskData);
      setTasks(prev => prev.map(t => t.id === editingTask.id ? updatedTask : t));
      setEditingTask(null);
    } catch (err) {
      logger.error('Error updating task:', err);
      // For now, we'll update the task locally since we don't have the API yet
      const updatedTask: Task = {
        ...editingTask,
        ...taskData,
        updatedAt: new Date().toISOString()
      };
      setTasks(prev => prev.map(t => t.id === editingTask.id ? updatedTask : t));
      setEditingTask(null);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await apiService.deleteTask(taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setDeletingTaskId(null);
    } catch (err) {
      logger.error('Error deleting task:', err);
      // For now, we'll delete the task locally since we don't have the API yet
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setDeletingTaskId(null);
    }
  };

  const handleAddFile = async (taskId: string, file: import('../types').TaskFile) => {
    try {
      // Save file to database
      await apiService.addTaskFile(taskId, file);
      
      // Real-time listeners will handle the file update automatically
    } catch (err) {
      logger.error('Error adding file:', err);
      // Could show a toast notification here
    }
  };

  const handleDeleteFile = async (taskId: string, fileId: string) => {
    try {
      // Delete file from database
      await apiService.deleteTaskFile(taskId, fileId);
      
      // Real-time listeners will handle the file deletion automatically
    } catch (err) {
      logger.error('Error deleting file:', err);
      // Could show a toast notification here
    }
  };



  const handleStatusChange = useCallback(async (taskId: string, status: Task['status']) => {
    try {
      const updatedTask = await apiService.updateTask(taskId, { status });
      
      // Update local state
      setTasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, ...updatedTask } : task
      ));
      
      // Reload hotel data to update progress
      if (hotel) {
        const updatedHotel = await apiService.getHotel(hotel.id);
        setHotel(updatedHotel);
      }
    } catch (err) {
      logger.error('Error updating task:', err);
      // Could show a toast notification here
    }
  }, [hotel]);

  const handleAddComment = useCallback(async (taskId: string, content: string, author: string) => {
    try {
      const newComment = await apiService.addTaskComment(taskId, content, author);
      
      // Update local state
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, comments: [...task.comments, newComment] }
          : task
      ));
    } catch (err) {
      logger.error('Error adding comment:', err);
    }
  }, []);

  const handleDeleteComment = useCallback(async (taskId: string, commentId: string) => {
    try {
      await apiService.deleteTaskComment(taskId, commentId);
      
      // Update local state
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, comments: task.comments.filter(comment => comment.id !== commentId) }
          : task
      ));
    } catch (err) {
      logger.error('Error deleting comment:', err);
    }
  }, []);

  const handleMarkAllComplete = useCallback(() => {
    // Get tasks that are not already completed
    const incompleteTasks = tasks.filter(task => task.status !== 'completed');
    
    if (incompleteTasks.length === 0) {
      // Show a success dialog instead of alert
      return;
    }

    setShowMarkAllCompleteDialog(true);
  }, [tasks]);

  const confirmMarkAllComplete = useCallback(async () => {
    try {
      const incompleteTasks = tasks.filter(task => task.status !== 'completed');
      
      // Update all incomplete tasks to completed
      const updatePromises = incompleteTasks.map(task => 
        apiService.updateTask(task.id, { status: 'completed' })
      );

      const updatedTasks = await Promise.all(updatePromises);
      
      // Update local state
      setTasks(prev => prev.map(task => {
        const updatedTask = updatedTasks.find(ut => ut.id === task.id);
        return updatedTask ? { ...task, ...updatedTask } : task;
      }));

      // Reload hotel data to update progress
      if (hotel) {
        const updatedHotel = await apiService.getHotel(hotel.id);
        setHotel(updatedHotel);
      }

      setShowMarkAllCompleteDialog(false);
    } catch (err) {
      logger.error('Error marking tasks as complete:', err);
      setShowMarkAllCompleteDialog(false);
    }
  }, [tasks, hotel]);

  const handleShowFeedbackForm = useCallback(() => {
    setShowFeedbackForm(true);
  }, []);

  const handleCloseFeedbackForm = useCallback(() => {
    setShowFeedbackForm(false);
  }, []);

  const handleSubmitFeedback = useCallback(async (feedbackData: any) => {
    try {
      if (editingFeedback) {
        await apiService.updateFeedback(editingFeedback.id, feedbackData);
        setEditingFeedback(null);
      } else {
        await apiService.createFeedback(feedbackData);
      }
      setShowFeedbackForm(false);
    } catch (error) {
      logger.error('Error submitting feedback:', error);
      throw error;
    }
  }, [editingFeedback]);

  const handleEditFeedback = useCallback((feedback: Feedback) => {
    setEditingFeedback(feedback);
    setShowFeedbackForm(true);
  }, []);

  const handleDeleteFeedback = useCallback((feedbackId: string) => {
    setDeletingFeedbackId(feedbackId);
  }, []);

  const confirmDeleteFeedback = useCallback(async () => {
    if (!deletingFeedbackId) return;
    
    try {
      await apiService.deleteFeedback(deletingFeedbackId);
      setDeletingFeedbackId(null);
    } catch (error) {
      logger.error('Error deleting feedback:', error);
    }
  }, [deletingFeedbackId]);


  const toggleSection = useCallback((categoryValue: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [categoryValue]: !prev[categoryValue]
    }));
  }, []);

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !hotel) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger-600 mb-4">{error || 'Hotel not found'}</p>
          {/* Only show back button if user has access to multiple hotels or is admin/management */}
          {(isAdmin || isManagement || (isHotelStaff && assignedHotelIds.length > 1)) && (
            <Button onClick={() => navigate('/')}>Back to Dashboard</Button>
          )}
        </div>
      </div>
    );
  }

  const stats = taskStats;



  return (
    <div className="min-h-screen">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 pt-8 pb-8">
        {/* Hotel Banner Card */}
        <div className="bg-white/20 backdrop-blur-2xl rounded-3xl border border-gray-200 shadow-lg mb-8 overflow-hidden">
          <div className="relative h-80">
            {/* Back Button - Floating over banner */}
            <div className="absolute top-6 left-6 z-10 flex space-x-3">
              {(isAdmin || isManagement || (isHotelStaff && assignedHotelIds.length > 1)) && (
                <Button
                  variant="secondary"
                  onClick={() => navigate('/')}
                  className="group bg-white/95 backdrop-blur-xl border border-white/30 text-gray-700 hover:bg-white hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 rounded-2xl px-4 py-2 font-medium shadow-lg"
                >
                  <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
                  {(isAdmin || isManagement) ? 'Back to Dashboard' : 'Back to Hotels'}
                </Button>
              )}

            </div>
            <img
              src={hotel.imageUrl}
              alt={hotel.name}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
            
            {/* Hotel Information Overlay */}
            <div className="absolute inset-0 flex items-end">
              <div className="w-full p-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-end">
                  {/* Hotel Details */}
                  <div className="lg:col-span-2 space-y-4">
                    <h1 className="text-5xl font-display font-bold text-white drop-shadow-md tracking-tight leading-none">
                      {hotel.name}
                    </h1>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3 bg-white/95 backdrop-blur-xl rounded-2xl px-4 py-3 border border-white/30 shadow-lg">
                        <User className="w-5 h-5 text-gray-700" />
                        <div>
                          <div className="text-sm text-gray-600 font-text font-medium">Contact Person</div>
                          <div className="text-lg font-display font-bold text-gray-900">{hotel.contactPerson}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 bg-white/95 backdrop-blur-xl rounded-2xl px-4 py-3 border border-white/30 shadow-lg">
                        <Calendar className="w-5 h-5 text-gray-700" />
                        <div>
                          <div className="text-sm text-gray-600 font-text font-medium">Started</div>
                          <div className="text-lg font-display font-bold text-gray-900">{formatDate(hotel.startDate || hotel.createdAt)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  

                </div>
              </div>
            </div>
          </div>
        </div>

                  {/* Progress Overview Card */}
        <div className="bg-white/20 backdrop-blur-2xl rounded-3xl border border-gray-200 shadow-lg mb-8 overflow-hidden">
            <div className="p-8">
              <div className="space-y-6">
                <div className="space-y-3">
                  <h2 className="text-3xl font-display font-bold text-gray-900">Deployment Progress</h2>
                  <p className="text-lg text-gray-600 font-text">Track the project deployment progress</p>
                </div>
                
                <div className="space-y-4">
                  <ProgressBar
                    value={hotel.progressPercentage}
                    color={hotel.progressPercentage >= 80 ? 'success' : 'primary'}
                    size="lg"
                    showLabel
                  />
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-3 bg-white/30 backdrop-blur-xl rounded-xl border border-white/20">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                      </div>
                    </div>
                    <div className="text-xl font-display font-bold text-gray-900 mb-1">{stats.completedTasks}</div>
                    <div className="text-xs font-text font-semibold text-gray-600 uppercase tracking-wider">Completed</div>
                  </div>
                  
                  <div className="text-center p-3 bg-white/30 backdrop-blur-xl rounded-xl border border-white/20">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                        <Clock className="w-4 h-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="text-xl font-display font-bold text-gray-900 mb-1">{stats.inProgressTasks}</div>
                    <div className="text-xs font-text font-semibold text-gray-600 uppercase tracking-wider">In Progress</div>
                  </div>
                  
                  <div className="text-center p-3 bg-white/30 backdrop-blur-xl rounded-xl border border-white/20">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-xl flex items-center justify-center">
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                      </div>
                    </div>
                    <div className="text-xl font-display font-bold text-gray-900 mb-1">{stats.notStartedTasks}</div>
                    <div className="text-xs font-text font-semibold text-gray-600 uppercase tracking-wider">Pending</div>
                  </div>
                  
                  <div className="text-center p-3 bg-white/30 backdrop-blur-xl rounded-xl border border-white/20">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center">
                        <XCircle className="w-4 h-4 text-red-600" />
                      </div>
                    </div>
                    <div className="text-xl font-display font-bold text-gray-900 mb-1">{stats.blockedTasks}</div>
                    <div className="text-xs font-text font-semibold text-gray-600 uppercase tracking-wider">Blocked</div>
                  </div>
                  
                  <div className="text-center p-3 bg-white/30 backdrop-blur-xl rounded-xl border border-white/20">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-gray-600" />
                      </div>
                    </div>
                    <div className="text-xl font-display font-bold text-gray-900 mb-1">{stats.totalTasks}</div>
                    <div className="text-xs font-text font-semibold text-gray-600 uppercase tracking-wider">Total</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

                  {/* Tasks Management Section */}
        <div className="bg-white/20 backdrop-blur-2xl rounded-3xl border border-gray-200 shadow-lg overflow-hidden">
            <div className="p-8 border-b border-white/20">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="space-y-2">
                  <h2 className="text-3xl font-display font-bold text-gray-900">Task Management</h2>
                  <p className="text-lg text-gray-600 font-text">
                    Monitor and manage deployment tasks for {hotel.name}
                  </p>
                </div>
                
                <div className="flex items-center space-x-4">
                  {/* Quick Actions */}
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => setShowAddTask(true)}
                      className="shadow-lg shadow-primary-400/25 hover:shadow-xl hover:shadow-primary-400/30"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Task
                    </Button>

                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleMarkAllComplete}
                      className="bg-white/40 backdrop-blur-xl border-gray-200"
                    >
                      Mark All Complete
                    </Button>
                  </div>
                  
                  {/* Task Counter */}
                  <div className="bg-white/30 backdrop-blur-xl px-6 py-3 rounded-2xl border border-white/20">
                    <div className="flex items-center space-x-3 text-gray-700">
                      <FileText className="w-5 h-5" />
                      <span className="font-text font-semibold">
                        {tasks.length} tasks
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-8">


              
              {/* Tasks Grid */}
              {tasks.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                    <FileText className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-2xl font-display font-bold text-gray-700 mb-3">
                    No Tasks Found
                  </h3>
                  <p className="text-lg text-gray-500 font-text max-w-md mx-auto leading-relaxed mb-6">
                    This hotel doesn't have any tasks yet. If you just created this hotel, the template tasks might still be loading.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Group tasks by category for better organization */}

                  
                  {sortedCategories.map((category, index) => {
                    const categoryTasks = tasks
                      .filter(task => task.category === category.id)
                      .sort((a, b) => (a.order || 0) - (b.order || 0));
                    logger.log(`🔍 Category "${category.name}" (${category.id}): found ${categoryTasks.length} tasks`);
                    if (categoryTasks.length === 0) return null;
                    
                    const completedTasks = categoryTasks.filter(task => task.status === 'completed').length;
                    const progressPercentage = Math.round((completedTasks / categoryTasks.length) * 100);
                    
                    return (
                      <div key={category.id} className="space-y-6">
                        <div className="bg-white/20 backdrop-blur-xl rounded-3xl border border-gray-200 overflow-hidden">
                          <button 
                            onClick={() => toggleSection(category.id)}
                            className="w-full p-6 text-left hover:bg-white/10 transition-all duration-200 focus:outline-none focus:bg-white/10"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                                  index % 4 === 0 ? 'bg-blue-100 text-blue-600' :
                                  index % 4 === 1 ? 'bg-emerald-100 text-emerald-600' :
                                  index % 4 === 2 ? 'bg-purple-100 text-purple-600' :
                                  'bg-orange-100 text-orange-600'
                                }`}>
                                  <span className="text-xl font-bold">{index + 1}</span>
                                </div>
                                <div>
                                  <h4 className="text-2xl font-display font-bold text-gray-900 capitalize">
                                    {category.name}
                                  </h4>
                                  <p className="text-gray-600 font-text">
                                    {completedTasks} of {categoryTasks.length} tasks completed
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-4">
                                <div className="text-right">
                                  <div className="text-3xl font-display font-bold text-gray-900 mb-1">
                                    {progressPercentage}%
                                  </div>
                                  <div className="w-20 bg-gray-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full transition-all duration-500 ${
                                        progressPercentage === 100 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' :
                                        progressPercentage > 50 ? 'bg-gradient-to-r from-blue-400 to-blue-500' :
                                        'bg-gradient-to-r from-orange-400 to-orange-500'
                                      }`}
                                      style={{ width: `${progressPercentage}%` }}
                                    />
                                  </div>
                                </div>
                                
                                <div className={`w-8 h-8 rounded-full bg-white/40 backdrop-blur-xl border border-gray-200 flex items-center justify-center transition-transform duration-200 ${
                                  collapsedSections[category.id] ? 'rotate-180' : ''
                                }`}>
                                  <ChevronDown className="w-4 h-4 text-gray-600" />
                                </div>
                              </div>
                            </div>
                          </button>
                        </div>
                        
                        {!collapsedSections[category.id] && (
                          <div className="px-6 pb-6">
                            <div className="grid gap-4">
                              {categoryTasks.map((task) => (
                                <TaskCard
                                  key={task.id}
                                  task={task}
                                  onStatusChange={handleStatusChange}
                                  onAddComment={handleAddComment}
                                  onDeleteComment={handleDeleteComment}
                                  onAddFile={handleAddFile}
                                  onDeleteFile={handleDeleteFile}
                                  onEditTask={setEditingTask}
                                  onDeleteTask={setDeletingTaskId}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Feedback Section */}
                              <div className="mt-8">
                      <FeedbackSection 
                        hotelId={hotel.id} 
                        hotelName={hotel.name} 
                        onShowForm={handleShowFeedbackForm}
                        onEdit={handleEditFeedback}
                        onDelete={handleDeleteFeedback}
                      />
                    </div>
        </div>

      {/* Add Task Modal */}
      {showAddTask && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-display font-bold text-neutral-900">
                Add New Task
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddTask(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <AddTaskForm
              categories={categories
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(category => ({
                  value: category.id,
                  label: category.name
                }))}
              onSave={handleAddTask}
              onCancel={() => setShowAddTask(false)}
            />
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-display font-bold text-neutral-900">
                Edit Task
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingTask(null)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <EditTaskForm
              task={editingTask}
              categories={categories
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(category => ({
                  value: category.id,
                  label: category.name
                }))}
              onSave={handleEditTask}
              onCancel={() => setEditingTask(null)}
            />
          </div>
        </div>
      )}

      {/* Delete Task Confirmation Modal */}
      {deletingTaskId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-display font-bold text-neutral-900 mb-4">
                Delete Task
              </h2>
              <p className="text-neutral-600 font-text mb-8">
                Are you sure you want to delete this task? This action cannot be undone.
              </p>
              <div className="flex items-center justify-center space-x-4">
                <Button 
                  variant="outline" 
                  onClick={() => setDeletingTaskId(null)}
                >
                  Cancel
                </Button>
                <Button 
                  variant="danger" 
                  onClick={() => handleDeleteTask(deletingTaskId)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Task
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Mark All Complete Dialog */}
      <ConfirmDialog
        isOpen={showMarkAllCompleteDialog}
        onClose={() => setShowMarkAllCompleteDialog(false)}
        onConfirm={confirmMarkAllComplete}
        title="Mark All Tasks Complete"
        message={`Are you sure you want to mark all ${tasks.filter(task => task.status !== 'completed').length} incomplete tasks as completed?`}
        variant="warning"
        confirmText="Mark Complete"
      />

                      {/* Feedback Form Modal */}
                <FeedbackForm
                  hotelId={hotel.id}
                  hotelName={hotel.name}
                  feedback={editingFeedback}
                  isOpen={showFeedbackForm}
                  onClose={handleCloseFeedbackForm}
                  onSubmit={handleSubmitFeedback}
                />

      {/* Delete Feedback Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deletingFeedbackId}
        onClose={() => setDeletingFeedbackId(null)}
        onConfirm={confirmDeleteFeedback}
        title="Delete Feedback"
        message="Are you sure you want to delete this feedback? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}

// Edit Task Form Component
interface EditTaskFormProps {
  task: Task;
  categories: { value: string; label: string }[];
  onSave: (taskData: Partial<Task>) => void;
  onCancel: () => void;
}

function EditTaskForm({ task, categories, onSave, onCancel }: EditTaskFormProps) {
  const [formData, setFormData] = useState({
    title: task.title,
    description: task.description,
    category: task.category,
    priority: task.priority,
    dueDate: task.dueDate,
    // Preserve all other task fields to prevent data loss
    status: task.status,
    assignedTo: task.assignedTo,
    order: task.order,
    hotelId: task.hotelId,
    createdAt: task.createdAt,
    comments: task.comments || [],
    files: task.files || [],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Task Title
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          placeholder="Enter task title"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Description
        </label>
        <RichTextEditor
          value={formData.description}
          onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
          placeholder="Enter task description"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          >
            {categories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
            Priority
          </label>
          <select
            value={formData.priority}
            onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as 'low' | 'medium' | 'high' }))}
            className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
            Due Date
          </label>
          <input
            type="date"
            value={formData.dueDate}
            onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
            className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="flex items-center justify-end space-x-4 pt-6">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          <Save className="w-4 h-4 mr-2" />
          Update Task
        </Button>
      </div>
    </form>
  );
}

// Add Task Form Component
interface AddTaskFormProps {
  categories: { value: string; label: string }[];
  onSave: (taskData: Partial<Task>) => void;
  onCancel: () => void;
}

function AddTaskForm({ categories, onSave, onCancel }: AddTaskFormProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: categories.length > 0 ? categories[0].value : '',
    priority: 'medium' as 'low' | 'medium' | 'high',
    dueDate: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Task Title
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          placeholder="Enter task title"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
          Description
        </label>
        <RichTextEditor
          value={formData.description}
          onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
          placeholder="Enter task description"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          >
            {categories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
            Priority
          </label>
          <select
            value={formData.priority}
            onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as 'low' | 'medium' | 'high' }))}
            className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-text font-semibold text-neutral-700 mb-2">
            Due Date
          </label>
          <input
            type="date"
            value={formData.dueDate}
            onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
            className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-neutral-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="flex items-center justify-end space-x-4 pt-6">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          <Save className="w-4 h-4 mr-2" />
          Create Task
        </Button>
      </div>
    </form>
  );
}
